import { useState } from 'react'
import {
  useConsumerUsers,
  useSuspendConsumer,
  useActivateConsumer,
  ConsumerUser,
} from '@/hooks/useAdminConsumers'

export default function ConsumerUsers() {
  const [search, setSearch] = useState('')
  const [tierFilter, setTierFilter] = useState<string>('')
  const [page, setPage] = useState(0)
  const limit = 25

  const { data, isLoading, error } = useConsumerUsers({
    search: search || undefined,
    tier: tierFilter || undefined,
    limit,
    offset: page * limit,
  })

  const suspendMutation = useSuspendConsumer()
  const activateMutation = useActivateConsumer()

  const handleToggleActive = (user: ConsumerUser) => {
    if (user.is_active) {
      suspendMutation.mutate(user.id)
    } else {
      activateMutation.mutate(user.id)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            className="w-full bg-[#161b22] border border-[#30363d] rounded px-4 py-2 text-[#c9d1d9] placeholder-[#484f58] font-mono text-sm focus:border-[#FFB84D]/50 focus:outline-none"
          />
        </div>
        <select
          value={tierFilter}
          onChange={(e) => { setTierFilter(e.target.value); setPage(0) }}
          className="bg-[#161b22] border border-[#30363d] rounded px-4 py-2 text-[#c9d1d9] font-mono text-sm focus:border-[#FFB84D]/50 focus:outline-none"
        >
          <option value="">All Tiers</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Results count */}
      {data && (
        <div className="text-[#8b949e] font-mono text-xs mb-4">
          Showing {data.users.length} of {data.total} consumers
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12 text-[#FFB84D] font-mono">Loading consumers...</div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded p-4 text-red-400 font-mono text-sm">
          Failed to load consumers: {(error as Error).message}
        </div>
      )}

      {/* Table */}
      {data && data.users.length > 0 && (
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#30363d]">
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Tier</th>
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Onboarded</th>
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Joined</th>
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Last Login</th>
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-[#8b949e] font-mono text-xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((user) => (
                <tr key={user.id} className="border-b border-[#30363d]/50 hover:bg-[#FFB84D]/5 transition-colors">
                  <td className="px-4 py-3 text-[#c9d1d9] font-mono text-sm">
                    {user.display_name || user.name}
                  </td>
                  <td className="px-4 py-3 text-[#8b949e] font-mono text-sm">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`
                      px-2 py-0.5 rounded text-xs font-mono uppercase
                      ${user.subscription_tier === 'free' ? 'bg-[#30363d] text-[#8b949e]' :
                        user.subscription_tier === 'pro' ? 'bg-[#FFB84D]/20 text-[#FFB84D]' :
                        'bg-purple-500/20 text-purple-400'}
                    `}>
                      {user.subscription_tier}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {user.onboarding_completed ? (
                      <span className="text-green-400">Yes</span>
                    ) : (
                      <span className="text-[#484f58]">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#8b949e] font-mono text-sm">{formatDate(user.created_at)}</td>
                  <td className="px-4 py-3 text-[#8b949e] font-mono text-sm">{formatDate(user.last_login)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-mono ${user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {user.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(user)}
                      disabled={suspendMutation.isPending || activateMutation.isPending}
                      className={`
                        px-3 py-1 rounded text-xs font-mono transition-colors
                        ${user.is_active
                          ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30'
                          : 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30'
                        }
                      `}
                    >
                      {user.is_active ? 'Suspend' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {data && data.users.length === 0 && (
        <div className="text-center py-12 bg-[#161b22] border border-[#30363d] rounded-lg">
          <p className="text-[#8b949e] font-mono">No consumers found</p>
        </div>
      )}

      {/* Pagination */}
      {data && data.total > limit && (
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-4 py-2 bg-[#161b22] border border-[#30363d] rounded text-[#c9d1d9] font-mono text-sm disabled:opacity-30 hover:border-[#FFB84D]/30"
          >
            Previous
          </button>
          <span className="text-[#8b949e] font-mono text-sm">
            Page {page + 1} of {Math.ceil(data.total / limit)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * limit >= data.total}
            className="px-4 py-2 bg-[#161b22] border border-[#30363d] rounded text-[#c9d1d9] font-mono text-sm disabled:opacity-30 hover:border-[#FFB84D]/30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
